/**
 * The wipe (spec 15.2, 17.7): after the API answered `user_deactivated`, `device_revoked`
 * or `user_revoked` and the person read the reason, every SATU byte on this device goes:
 * the worker releases its OPFS handles and stops, the `.simu` directory and the two folders an
 * older release's photo reading wrote (`bacaan`, `dokumen`: removed at the first start of
 * release 27, and here again in case that start was refused) are removed, the
 * stored Supabase session, what the old reading kept in the browser's storage, the
 * display copies and the places of work (they name accounts), the kept report answers and the
 * shell caches are cleared,
 * then the tab reloads on /masuk so a fresh worker opens an empty database. Nothing here runs
 * on a plain refresh failure; that path only sets `perluMasukKembali`.
 */

import { sapuSisaBacaPeramban } from '../app/sisaBaca'
import { lepasPenjagaTab } from '../app/store/jaga'
import { PAROKI_CACHE_KEY } from '../app/store/sesi'
import { TAMPILAN_KEY } from '../app/store/tampilan'
import { TEMPAT_KERJA_KEY } from '../app/store/tempatKerja'
import type { DbClient } from '../db/client'
import { DIREKTORI_SISA_BACA } from '../db/sisaBaca'
import { kunciCacheLaporan } from '../lib/cacheLaporan'
import { AUTH_STORAGE_KEY } from './supabase'

/** The OPFS pool directory the worker uses (db/worker.ts POOL.directory). */
// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const OPFS_DIRECTORY = '.simu'

export interface WipePlatform {
  removeOpfsDirectory(name: string): Promise<void>
  clearCaches(): Promise<void>
  removeLocal(keys: string[]): void
  reload(path: string): void
}

const domPlatform: WipePlatform = {
  async removeOpfsDirectory(name) {
    const root = await navigator.storage.getDirectory()
    await root.removeEntry(name, { recursive: true })
  },
  async clearCaches() {
    if (typeof caches === 'undefined') return
    const keys = await caches.keys()
    await Promise.all(keys.map((key) => caches.delete(key)))
  },
  removeLocal(keys) {
    for (const key of keys) localStorage.removeItem(key)
  },
  reload(path) {
    // Nothing is left to save: the browser's "leave site?" question must not hold the wipe.
    lepasPenjagaTab()
    location.replace(path)
  },
}

export async function wipeDevice(
  db: Pick<DbClient, 'request' | 'close'>,
  platform: WipePlatform = domPlatform,
): Promise<void> {
  try {
    await db.request('db:pause', {})
  } catch {
    // Already paused or gone; closing releases what is left.
  }
  db.close()
  // The database, and whatever an older release's photo reading left beside it in OPFS.
  for (const direktori of [OPFS_DIRECTORY, ...DIREKTORI_SISA_BACA]) {
    try {
      await platform.removeOpfsDirectory(direktori)
    } catch {
      // No OPFS (memory database) or already gone.
    }
  }
  // Review C-1: the report answers a diocese role kept (`simu:laporan:*`) go too.
  platform.removeLocal([
    AUTH_STORAGE_KEY,
    PAROKI_CACHE_KEY,
    TAMPILAN_KEY,
    TEMPAT_KERJA_KEY,
    ...kunciCacheLaporan(),
  ])
  // What the old reading kept in localStorage and this tab's sessionStorage.
  sapuSisaBacaPeramban()
  try {
    await platform.clearCaches()
  } catch {
    // Caches are a convenience; the data is what matters.
  }
  platform.reload('/masuk')
}
